import {AxiosInstance, AxiosResponse} from 'axios'

export interface ResearchOperation {
    operation_id: string
    status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED'
    status_url: string
    pesquisa_id?: string | null
    id?: string | null
    artifact_url?: string | null
}

export class ResearchOperationError extends Error {}

export const isOperation = (value: any): value is ResearchOperation =>
    typeof value?.operation_id === 'string' && typeof value?.status_url === 'string'

export function checkAbort(signal: AbortSignal) {
    if (signal.aborted) throw new DOMException('Acompanhamento cancelado.', 'AbortError')
}

// Axios baseURL already includes /api/priceresearch/v1. Only accept this API's
// operation routes; never forward its bearer token to an arbitrary supplied URL.
export function operationPath(url: string): string {
    const path = url.replace(/^\/api\/priceresearch\/v1(?=\/)/, '')
    if (!/^\/pesquisas\/operacoes\/[^/?#]+(?:\/artefato)?$/.test(path)) {
        throw new ResearchOperationError('Endereço de operação inválido.')
    }
    return path
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
    checkAbort(signal)
    return new Promise((resolve, reject) => {
        const cancel = () => {
            clearTimeout(timer)
            reject(new DOMException('Acompanhamento cancelado.', 'AbortError'))
        }
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', cancel)
            resolve()
        }, ms)
        signal.addEventListener('abort', cancel, {once: true})
    })
}

// Ten minutes total, at most three consecutive transient read retries. Commands
// are never retried automatically: doing so could enqueue another operation.
export async function pollResearch<T>(
    read: (timeout: number) => Promise<T>,
    done: (value: T) => boolean,
    signal: AbortSignal,
): Promise<T> {
    const deadline = Date.now() + 600000
    let failures = 0
    while (Date.now() < deadline) {
        checkAbort(signal)
        let value: T
        try {
            value = await read(Math.min(30000, deadline - Date.now()))
            checkAbort(signal)
            failures = 0
        } catch (error: any) {
            checkAbort(signal)
            const status = error?.response?.status
            if ((status && status !== 429 && status < 500) || ++failures > 3) {
                throw new ResearchOperationError('Não foi possível consultar o processamento. Atualize a tela para conferir o estado antes de tentar novamente.')
            }
            await delay(Math.min(3000, Math.max(0, deadline - Date.now())), signal)
            continue
        }
        if (done(value)) return value
        await delay(Math.min(3000, Math.max(0, deadline - Date.now())), signal)
    }
    throw new ResearchOperationError('O acompanhamento excedeu 10 minutos. O processamento pode continuar no servidor; confira o estado antes de tentar novamente.')
}

export function waitOperation(api: AxiosInstance, operation: ResearchOperation, signal: AbortSignal) {
    return pollResearch<ResearchOperation>(
        async (timeout) => (await api.get(operationPath(operation.status_url), {signal, timeout})).data,
        (value) => {
            if (value.status === 'CANCELLED') throw new ResearchOperationError('A operação foi cancelada no servidor.')
            if (value.status === 'FAILED') throw new ResearchOperationError('A operação falhou. Confira a pesquisa antes de tentar novamente.')
            if (!['PENDING', 'RUNNING', 'SUCCEEDED'].includes(value.status)) throw new ResearchOperationError('Estado de operação desconhecido.')
            return value.status === 'SUCCEEDED'
        },
        signal,
    )
}

export async function acceptedOperation(response: AxiosResponse): Promise<ResearchOperation | null> {
    if (response.status !== 202) return null
    let value = response.data
    const contentType = String(response.headers?.['content-type'] || value?.type || '')
    if (value instanceof Blob && /json/i.test(contentType)) {
        value = JSON.parse(await value.text())
    }
    if (!isOperation(value)) throw new ResearchOperationError('Resposta de operação inválida; nenhum arquivo foi baixado.')
    return value
}
